import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { 
  Settings, 
  Shield, 
  Server,
  Save,
  AlertTriangle,
  ExternalLink,
  Info,
  MessageCircle,
  Eye,
  EyeOff,
  Trash2,
  CheckCircle,
  RefreshCw,
  Database,
  GitBranch,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const { tenant, refreshTenant } = useAuth();
  const [settings, setSettings] = useState({
    default_llm_provider: 'openai',
    default_embeddings_provider: 'openai',
    llm_model: 'gpt-4o',
    embeddings_model: 'text-embedding-3-small',
    ollama_url: ''
  });
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Twilio settings
  const [twilioSettings, setTwilioSettings] = useState({
    account_sid: '',
    auth_token: '',
    whatsapp_number: ''
  });
  const [twilioStatus, setTwilioStatus] = useState(null);
  const [savingTwilio, setSavingTwilio] = useState(false);
  const [showAuthToken, setShowAuthToken] = useState(false);
  
  // System info state
  const [systemInfo, setSystemInfo] = useState(null);
  const [loadingSystemInfo, setLoadingSystemInfo] = useState(false);
  
  // Scheduler status state
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [serviceAlertStatus, setServiceAlertStatus] = useState(null);
  const [loadingScheduler, setLoadingScheduler] = useState(false);
  const [restartingScheduler, setRestartingScheduler] = useState(false);
  const [runningAlert, setRunningAlert] = useState(null);

  useEffect(() => {
    if (tenant) {
      setSettings({
        default_llm_provider: tenant.default_llm_provider || 'openai',
        default_embeddings_provider: tenant.default_embeddings_provider || 'openai',
        llm_model: tenant.llm_model || 'gpt-4o',
        embeddings_model: tenant.embeddings_model || 'text-embedding-3-small',
        ollama_url: tenant.ollama_url || ''
      });
    }
    fetchTwilioStatus();
    fetchSystemInfo();
    fetchSchedulerStatus();
    fetchServiceAlertStatus();
  }, [tenant]);

  const fetchSchedulerStatus = async () => {
    setLoadingScheduler(true);
    try {
      const response = await axios.get(`${API}/fleetio/inspection-scheduler-status`);
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch scheduler status:', error);
      setSchedulerStatus({ scheduler_running: false, message: 'Failed to fetch status' });
    } finally {
      setLoadingScheduler(false);
    }
  };

  const fetchServiceAlertStatus = async () => {
    try {
      const response = await axios.get(`${API}/titan-gps/daily-alert-scheduler/status`);
      setServiceAlertStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch service alert status:', error);
      setServiceAlertStatus({ scheduler_running: false, message: 'Failed to fetch status' });
    }
  };

  const handleRestartScheduler = async () => {
    setRestartingScheduler(true);
    try {
      const token = localStorage.getItem('vaultai_token');
      const response = await axios.post(`${API}/fleetio/inspection-scheduler-restart`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success('Inspection scheduler restarted successfully');
        fetchSchedulerStatus();
      } else {
        toast.error(response.data.message || 'Failed to restart scheduler');
      }
    } catch (error) {
      toast.error('Failed to restart scheduler');
    } finally {
      setRestartingScheduler(false);
    }
  };

  const handleRunInspectionCheck = async () => {
    setRunningAlert('inspection');
    try {
      const token = localStorage.getItem('vaultai_token');
      const response = await axios.post(`${API}/fleetio/inspections/check-defects`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.alerts_sent > 0) {
        toast.success(`Sent ${response.data.alerts_sent} inspection alert(s)`);
      } else {
        toast.info('No new inspection defects found');
      }
    } catch (error) {
      toast.error('Failed to run inspection check');
    } finally {
      setRunningAlert(null);
    }
  };

  const handleRun400hrAlert = async () => {
    setRunningAlert('400hr');
    try {
      const token = localStorage.getItem('vaultai_token');
      // Use force=true to bypass "already sent today" check
      const response = await axios.post(`${API}/titan-gps/daily-alert/run-now?force=true`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const unitsCount = response.data.result?.units_count || 0;
        const status = response.data.result?.status || 'completed';
        if (status === 'no_alerts_needed') {
          toast.info('No units need service alerts (all above threshold)');
        } else if (unitsCount > 0) {
          toast.success(`400hr service alert sent! (${unitsCount} units)`);
        } else {
          toast.success('400hr service alert executed');
        }
        fetchServiceAlertStatus();
      } else {
        toast.error(response.data.error || 'Failed to run alert');
      }
    } catch (error) {
      console.error('400hr alert error:', error);
      toast.error(error.response?.data?.detail || 'Failed to run 400hr alert');
    } finally {
      setRunningAlert(null);
    }
  };

  const handleRestartServiceAlert = async () => {
    try {
      const token = localStorage.getItem('vaultai_token');
      await axios.post(`${API}/titan-gps/daily-alert-scheduler/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Service alert scheduler restarted');
      fetchServiceAlertStatus();
    } catch (error) {
      toast.error('Failed to restart service alert scheduler');
    }
  };

  const fetchSystemInfo = async () => {
    setLoadingSystemInfo(true);
    try {
      const response = await axios.get(`${API}/admin/system-info`);
      setSystemInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch system info:', error);
    } finally {
      setLoadingSystemInfo(false);
    }
  };

  const handleForceRefresh = () => {
    // Clear all caches and reload
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    // Clear localStorage cache flags if any
    localStorage.removeItem('app_version');
    // Force reload from server
    window.location.reload(true);
  };

  const fetchTwilioStatus = async () => {
    try {
      const response = await axios.get(`${API}/settings/integrations/twilio`);
      setTwilioStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch Twilio status:', error);
    }
  };

  const handleSaveTwilio = async () => {
    if (!twilioSettings.account_sid || !twilioSettings.auth_token || !twilioSettings.whatsapp_number) {
      toast.error('Please fill in all Twilio fields');
      return;
    }

    setSavingTwilio(true);
    try {
      const formData = new FormData();
      formData.append('account_sid', twilioSettings.account_sid);
      formData.append('auth_token', twilioSettings.auth_token);
      formData.append('whatsapp_number', twilioSettings.whatsapp_number);
      
      await axios.post(`${API}/settings/integrations/twilio`, formData);
      toast.success('Twilio settings saved successfully');
      setTwilioSettings({ account_sid: '', auth_token: '', whatsapp_number: '' });
      fetchTwilioStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Twilio settings');
    } finally {
      setSavingTwilio(false);
    }
  };

  const handleDeleteTwilio = async () => {
    if (!window.confirm('Are you sure you want to remove Twilio integration?')) return;
    
    try {
      await axios.delete(`${API}/settings/integrations/twilio`);
      toast.success('Twilio settings removed');
      setTwilioStatus(null);
    } catch (error) {
      toast.error('Failed to remove Twilio settings');
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings);
      await refreshTenant();
      setHasChanges(false);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const isPrivateMode = settings.default_llm_provider !== 'openai' && 
                        settings.default_embeddings_provider !== 'openai';

  const requiresOllamaUrl = settings.default_llm_provider === 'ollama' || 
                            settings.default_embeddings_provider === 'ollama';

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-foreground">Settings</h1>
          <p className="text-muted-foreground mt-1">
            Configure your workspace privacy and AI providers
          </p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={!hasChanges || saving}
          className="btn-glow"
          data-testid="save-settings-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Privacy Mode Indicator */}
      <Card className={isPrivateMode ? 'border-emerald-500/30' : 'border-amber-500/30'}>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              isPrivateMode ? 'bg-emerald-500/10' : 'bg-amber-500/10'
            }`}>
              <Shield className={`w-6 h-6 ${isPrivateMode ? 'text-emerald-400' : 'text-amber-400'}`} />
            </div>
            <div className="flex-1">
              <h3 className={`font-semibold ${isPrivateMode ? 'text-emerald-400' : 'text-amber-400'}`}>
                {isPrivateMode ? 'Private SaaS Mode' : 'Cloud Mode'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {isPrivateMode 
                  ? 'No data sent to third parties. Using local/Ollama for all AI operations.'
                  : 'Questions and document snippets are sent to OpenAI for processing.'
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* What is an LLM - Educational */}
      <Card className="bg-secondary/30 border-primary/20">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Info className="w-4 h-4 text-primary" />
            Understanding AI Components
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div>
            <p className="font-medium text-foreground">What is an LLM (Large Language Model)?</p>
            <p className="text-muted-foreground mt-1">
              The AI that understands your questions and generates human-like answers. Think of it as the brain that reads your document snippets and writes responses. Examples: GPT-5.1, Llama 3, Claude.
            </p>
          </div>
          <div>
            <p className="font-medium text-foreground">What are Embeddings?</p>
            <p className="text-muted-foreground mt-1">
              A way to convert text into numbers so computers can find similar content. When you ask about the vacation policy, embeddings help find the most relevant paragraphs in your documents. This happens during document indexing and every query.
            </p>
          </div>
          <div>
            <p className="font-medium text-foreground">Why does this matter for privacy?</p>
            <p className="text-muted-foreground mt-1">
              <strong>Cloud mode:</strong> Your text is sent to OpenAI servers for processing.<br/>
              <strong>Private SaaS mode:</strong> All processing happens on your Ollama server - nothing leaves your infrastructure.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* LLM Provider Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            LLM Provider
          </CardTitle>
          <CardDescription>
            Choose the AI model for answering questions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Provider</Label>
              <Select 
                value={settings.default_llm_provider} 
                onValueChange={(v) => handleChange('default_llm_provider', v)}
              >
                <SelectTrigger data-testid="llm-provider-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="openai">OpenAI (Cloud)</SelectItem>
                  <SelectItem value="ollama">Ollama (Local)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Model</Label>
              <Select 
                value={settings.llm_model} 
                onValueChange={(v) => handleChange('llm_model', v)}
              >
                <SelectTrigger data-testid="llm-model-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {settings.default_llm_provider === 'openai' ? (
                    <>
                      <SelectItem value="gpt-5.1">GPT-5.1 (Latest)</SelectItem>
                      <SelectItem value="gpt-5">GPT-5</SelectItem>
                      <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                      <SelectItem value="gpt-4o-mini">GPT-4o Mini</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="llama3">Llama 3</SelectItem>
                      <SelectItem value="llama3:70b">Llama 3 70B</SelectItem>
                      <SelectItem value="mistral">Mistral</SelectItem>
                      <SelectItem value="mixtral">Mixtral</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {settings.default_llm_provider === 'openai' && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-200">
                <p className="font-medium">Third-party processing enabled</p>
                <p className="text-amber-300/80 mt-1">
                  Your questions and relevant document snippets will be sent to OpenAI servers.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Embeddings Provider Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Embeddings Provider
          </CardTitle>
          <CardDescription>
            Choose how documents are indexed for search
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Provider</Label>
              <Select 
                value={settings.default_embeddings_provider} 
                onValueChange={(v) => handleChange('default_embeddings_provider', v)}
              >
                <SelectTrigger data-testid="embeddings-provider-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="openai">OpenAI (Cloud)</SelectItem>
                  <SelectItem value="ollama">Ollama (Local)</SelectItem>
                  <SelectItem value="local">Local (sentence-transformers)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Model</Label>
              <Select 
                value={settings.embeddings_model} 
                onValueChange={(v) => handleChange('embeddings_model', v)}
              >
                <SelectTrigger data-testid="embeddings-model-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {settings.default_embeddings_provider === 'openai' ? (
                    <>
                      <SelectItem value="text-embedding-3-small">text-embedding-3-small</SelectItem>
                      <SelectItem value="text-embedding-3-large">text-embedding-3-large</SelectItem>
                    </>
                  ) : settings.default_embeddings_provider === 'ollama' ? (
                    <>
                      <SelectItem value="nomic-embed-text">nomic-embed-text</SelectItem>
                      <SelectItem value="mxbai-embed-large">mxbai-embed-large</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="all-MiniLM-L6-v2">all-MiniLM-L6-v2</SelectItem>
                      <SelectItem value="all-mpnet-base-v2">all-mpnet-base-v2</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {settings.default_embeddings_provider === 'openai' && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-200">
                <p className="font-medium">Document content sent to OpenAI</p>
                <p className="text-amber-300/80 mt-1">
                  Document text is sent to OpenAI to generate embeddings during indexing.
                </p>
              </div>
            </div>
          )}

          <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
            <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-200">
              <p className="font-medium">Re-indexing required</p>
              <p className="text-blue-300/80 mt-1">
                Changing the embeddings provider requires re-indexing all documents. This happens automatically in the background.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ollama Configuration */}
      {requiresOllamaUrl && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="w-5 h-5" />
              Ollama Configuration
            </CardTitle>
            <CardDescription>
              Configure your Ollama server endpoint
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ollama-url">Ollama Server URL</Label>
              <Input
                id="ollama-url"
                placeholder="http://localhost:11434"
                value={settings.ollama_url}
                onChange={(e) => handleChange('ollama_url', e.target.value)}
                data-testid="ollama-url-input"
              />
              <p className="text-xs text-muted-foreground">
                Enter the URL where your Ollama instance is running
              </p>
            </div>

            <Link to="/ollama-guide" target="_blank">
              <Button variant="outline" className="gap-2" data-testid="ollama-guide-link">
                <ExternalLink className="w-4 h-4" />
                View Ollama Setup Guide
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Ollama Help Section */}
      <Card className="bg-secondary/30">
        <CardHeader>
          <CardTitle className="text-base">Setting Up Ollama for Private Mode</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div>
            <p className="font-medium text-foreground">What you need:</p>
            <ul className="list-disc list-inside text-muted-foreground mt-1 space-y-1">
              <li>A server or computer with Ollama installed</li>
              <li>At least one LLM model (e.g., <code className="bg-background px-1 rounded">llama3</code>)</li>
              <li>An embeddings model (e.g., <code className="bg-background px-1 rounded">nomic-embed-text</code>)</li>
              <li>Network access from this app to your Ollama server</li>
            </ul>
          </div>
          <div>
            <p className="font-medium text-foreground">Quick Install (Mac/Linux):</p>
            <code className="block bg-background p-3 rounded mt-1 text-xs">
              curl -fsSL https://ollama.com/install.sh | sh<br/>
              ollama pull llama3<br/>
              ollama pull nomic-embed-text
            </code>
          </div>
          <div>
            <p className="font-medium text-foreground">Typical URL format:</p>
            <p className="text-muted-foreground mt-1">
              <code className="bg-background px-1 rounded">http://your-server-ip:11434</code> (default port is 11434)
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Twilio WhatsApp Integration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-emerald-400" />
            WhatsApp Integration (Twilio)
          </CardTitle>
          <CardDescription>
            Connect WhatsApp for driver communication via Twilio
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current Status */}
          {twilioStatus?.configured ? (
            <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <div>
                    <p className="font-medium text-emerald-400">Twilio Connected</p>
                    <p className="text-xs text-muted-foreground">
                      Account: {twilioStatus.account_sid} • Number: {twilioStatus.whatsapp_number}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleDeleteTwilio}
                  className="text-red-400 hover:text-red-300"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                <div>
                  <p className="font-medium text-yellow-400">Not Configured</p>
                  <p className="text-xs text-muted-foreground">
                    Add your Twilio credentials below to enable WhatsApp
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Configuration Form */}
          <div className="space-y-4 pt-4 border-t border-border">
            <div className="space-y-2">
              <Label htmlFor="twilio-sid">Account SID</Label>
              <Input
                id="twilio-sid"
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                value={twilioSettings.account_sid}
                onChange={(e) => setTwilioSettings({ ...twilioSettings, account_sid: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Found in your Twilio Console dashboard
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="twilio-token">Auth Token</Label>
              <div className="relative">
                <Input
                  id="twilio-token"
                  type={showAuthToken ? "text" : "password"}
                  placeholder="Your Twilio Auth Token"
                  value={twilioSettings.auth_token}
                  onChange={(e) => setTwilioSettings({ ...twilioSettings, auth_token: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => setShowAuthToken(!showAuthToken)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showAuthToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="twilio-number">WhatsApp Number</Label>
              <Input
                id="twilio-number"
                placeholder="+14155238886"
                value={twilioSettings.whatsapp_number}
                onChange={(e) => setTwilioSettings({ ...twilioSettings, whatsapp_number: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Your Twilio WhatsApp sandbox or business number (with country code)
              </p>
            </div>

            <Button 
              onClick={handleSaveTwilio} 
              disabled={savingTwilio}
              className="w-full"
            >
              <Save className="w-4 h-4 mr-2" />
              {savingTwilio ? 'Saving...' : 'Save Twilio Settings'}
            </Button>
          </div>

          {/* Help Section */}
          <div className="pt-4 border-t border-border space-y-4">
            <div>
              <p className="text-sm font-medium text-foreground mb-2">Setup Instructions (Sandbox):</p>
              <ol className="list-decimal list-inside text-xs text-muted-foreground space-y-1">
                <li>Sign up at <a href="https://www.twilio.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">twilio.com</a></li>
                <li>Go to Messaging → Try it Out → WhatsApp Sandbox</li>
                <li>Copy your Account SID and Auth Token from the dashboard</li>
                <li>Use the sandbox number: <code className="bg-background px-1 rounded">whatsapp:+14155238886</code></li>
                <li>In Twilio sandbox settings, set webhook URL to:<br/>
                  <code className="bg-background px-1 rounded">{process.env.REACT_APP_BACKEND_URL}/api/whatsapp/webhook</code>
                </li>
              </ol>
            </div>
            
            {/* Important Note for Proactive Messages */}
            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs">
                  <p className="font-medium text-blue-400">Important: For Override Notifications</p>
                  <p className="text-blue-300/80 mt-1">
                    To receive override approval/denial notifications, drivers must first send a message to your WhatsApp number.
                    For Sandbox testing, drivers need to send <code className="bg-background/50 px-1 rounded">join &lt;your-sandbox-code&gt;</code> to +1 415 523 8886 before they can receive notifications.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="w-full gap-2">
              <ExternalLink className="w-4 h-4" />
              Open Twilio Console
            </Button>
          </a>
        </CardContent>
      </Card>

      {/* System Info Card - Admin Only */}
      {systemInfo && (
        <Card className="border-primary/30">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Server className="w-5 h-5 text-primary" />
                <CardTitle>System Information</CardTitle>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchSystemInfo}
                disabled={loadingSystemInfo}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingSystemInfo ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            <CardDescription>
              Current deployment version and database stats
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Version Info */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <GitBranch className="w-4 h-4 text-muted-foreground" />
                Version
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Commit</p>
                  <p className="font-mono font-medium text-green-400">{systemInfo.version?.commit || 'unknown'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Branch</p>
                  <p className="font-medium">{systemInfo.version?.branch || 'unknown'}</p>
                </div>
                <div className="col-span-2 bg-green-500/10 border border-green-500/30 rounded p-2">
                  <p className="text-muted-foreground text-xs">Build Time (MST)</p>
                  <p className="font-medium text-sm text-green-400">{systemInfo.version?.build_time_mst || 'unknown'}</p>
                </div>
              </div>
            </div>

            {/* Database Stats */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <Database className="w-4 h-4 text-muted-foreground" />
                Database
              </h4>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="bg-secondary/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-primary">{systemInfo.database?.users || 0}</p>
                  <p className="text-xs text-muted-foreground">Users</p>
                </div>
                <div className="bg-secondary/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-400">{systemInfo.database?.form_templates || 0}</p>
                  <p className="text-xs text-muted-foreground">Form Templates</p>
                </div>
                <div className="bg-secondary/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-400">{systemInfo.database?.form_submissions || 0}</p>
                  <p className="text-xs text-muted-foreground">Submissions</p>
                </div>
              </div>
              {/* Dispatch Stats */}
              <div className="grid grid-cols-3 gap-3 text-sm mt-2">
                <div className="bg-purple-500/20 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-purple-400">{systemInfo.database?.dispatch_jobs || 0}</p>
                  <p className="text-xs text-muted-foreground">Jobs</p>
                </div>
                <div className="bg-purple-500/20 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-purple-400">{systemInfo.database?.dispatch_drivers || 0}</p>
                  <p className="text-xs text-muted-foreground">Drivers</p>
                </div>
                <div className="bg-purple-500/20 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-purple-400">{systemInfo.database?.dispatch_assignments || 0}</p>
                  <p className="text-xs text-muted-foreground">Assignments</p>
                </div>
              </div>
              {/* GPS Stats */}
              <div className="grid grid-cols-2 gap-3 text-sm mt-2">
                <div className="bg-amber-500/20 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-amber-400">{systemInfo.database?.vehicle_landmark_states || 0}</p>
                  <p className="text-xs text-muted-foreground">GPS Tracked Vehicles</p>
                </div>
                <div className="bg-amber-500/20 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-amber-400">{systemInfo.database?.landmark_events || 0}</p>
                  <p className="text-xs text-muted-foreground">Landmark Events</p>
                </div>
              </div>
            </div>

            {/* Server Info */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                Server
              </h4>
              <div className="text-sm space-y-1">
                <p className="text-muted-foreground">Uptime: <span className="text-foreground font-medium">{systemInfo.server?.uptime_hours} hours</span></p>
                <p className="text-muted-foreground">Server Time (MST): <span className="text-foreground font-medium">{systemInfo.server?.current_time_mst || 'unknown'}</span></p>
                <p className="text-muted-foreground">MST Date: <span className="text-foreground font-medium text-green-400">{systemInfo.server?.mst_date || 'unknown'}</span></p>
                <p className="text-muted-foreground text-xs mt-2">Last checked: {new Date(systemInfo.timestamp).toLocaleString()}</p>
              </div>
            </div>

            {/* Background Schedulers Status */}
            <div className="space-y-3 pt-4 border-t border-border">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-muted-foreground" />
                  Background Schedulers
                </h4>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => { fetchSchedulerStatus(); fetchServiceAlertStatus(); }}
                  disabled={loadingScheduler}
                >
                  <RefreshCw className={`w-3 h-3 ${loadingScheduler ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              
              {/* 400hr Service Alert Scheduler */}
              <div className={`rounded-lg p-3 ${serviceAlertStatus?.scheduler_running ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${serviceAlertStatus?.scheduler_running ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                    <span className="font-medium text-sm">400hr Service Alerts</span>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded ${serviceAlertStatus?.scheduler_running ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {serviceAlertStatus?.scheduler_running ? 'RUNNING' : 'STOPPED'}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>Scheduled: {serviceAlertStatus?.alert_time || '7:00 AM MST daily'}</p>
                  <p>Threshold: {serviceAlertStatus?.threshold_hours || 10} hours remaining</p>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button 
                    variant="default" 
                    size="sm"
                    className="flex-1 gap-2 bg-amber-600 hover:bg-amber-700"
                    onClick={handleRun400hrAlert}
                    disabled={runningAlert === '400hr'}
                  >
                    <RefreshCw className={`w-3 h-3 ${runningAlert === '400hr' ? 'animate-spin' : ''}`} />
                    {runningAlert === '400hr' ? 'Running...' : 'Run Now'}
                  </Button>
                  {!serviceAlertStatus?.scheduler_running && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="flex-1"
                      onClick={handleRestartServiceAlert}
                    >
                      Restart
                    </Button>
                  )}
                </div>
              </div>

              {/* Inspection Defect Scheduler */}
              {schedulerStatus && (
                <div className={`rounded-lg p-3 ${schedulerStatus.scheduler_running ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${schedulerStatus.scheduler_running ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                      <span className="font-medium text-sm">Inspection Defect Alerts</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded ${schedulerStatus.scheduler_running ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                      {schedulerStatus.scheduler_running ? 'RUNNING' : 'STOPPED'}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>Check Interval: {schedulerStatus.check_interval || '5 minutes'}</p>
                    {schedulerStatus.jobs?.map((job, idx) => (
                      <p key={idx}>Next Run: {job.next_run ? new Date(job.next_run).toLocaleString() : 'Not scheduled'}</p>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button 
                      variant="default" 
                      size="sm"
                      className="flex-1 gap-2 bg-blue-600 hover:bg-blue-700"
                      onClick={handleRunInspectionCheck}
                      disabled={runningAlert === 'inspection'}
                    >
                      <RefreshCw className={`w-3 h-3 ${runningAlert === 'inspection' ? 'animate-spin' : ''}`} />
                      {runningAlert === 'inspection' ? 'Running...' : 'Run Now'}
                    </Button>
                    {!schedulerStatus.scheduler_running && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex-1"
                        onClick={handleRestartScheduler}
                        disabled={restartingScheduler}
                      >
                        {restartingScheduler ? 'Restarting...' : 'Restart'}
                      </Button>
                    )}
                  </div>
                </div>
              )}
              
              {schedulerStatus?.message && !schedulerStatus.scheduler_running && (
                <p className="text-xs text-red-400">{schedulerStatus.message}</p>
              )}
              
              {!schedulerStatus && !loadingScheduler && (
                <p className="text-sm text-muted-foreground">Unable to fetch scheduler status</p>
              )}
            </div>

            {/* Force Refresh Button */}
            <div className="pt-4 border-t border-border">
              <Button 
                variant="destructive" 
                className="w-full gap-2"
                onClick={handleForceRefresh}
              >
                <RefreshCw className="w-4 h-4" />
                Force Refresh (Clear Cache & Reload)
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Use this if you are not seeing the latest changes after a deploy
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
